<template>
  <div>
    <v-navigation-drawer
        id="core-navigation-drawer"
        v-model="drawerVisible"
        :color="drawerColor"
        class="app-drawer"
        app
        dark
        width="280"
        :mini-variant="!drawer"
        mini-variant-width="72"
    >
    <!-- Brand Logo Section -->
    <div class="brand-section">
      <div v-if="drawer" class="brand-content">
        <div class="brand-logo">
          <v-icon size="28" color="white">mdi-hospital-building</v-icon>
        </div>
        <div class="brand-text">
          <span class="brand-name">HRIS HRD</span>
          <span class="brand-tagline">Management System</span>
        </div>
        <v-btn icon x-small class="collapse-btn" @click="toggleDrawer">
          <v-icon size="16">mdi-chevron-left</v-icon>
        </v-btn>
      </div>
      <div v-else class="brand-mini">
        <v-btn icon small class="expand-btn" @click="toggleDrawer">
          <v-icon size="20">mdi-menu</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- User Profile Card -->
    <div class="user-section" :class="{ 'user-section--mini': !drawer }">
      <div v-if="drawer" class="user-profile-card">
        <div class="user-profile-content">
          <div class="avatar-wrapper">
            <v-avatar size="40" class="drawer-avatar">
              <span class="avatar-initials">{{ userInitials }}</span>
            </v-avatar>
            <span class="status-indicator"></span>
          </div>
          <div class="user-info">
            <span class="drawer-user-label">Welcome back</span>
            <span class="drawer-username">{{ loggedInUser?.data?.username }}</span>
          </div>
        </div>
      </div>
      <div v-else class="user-mini">
        <v-tooltip right>
          <template v-slot:activator="{ on, attrs }">
            <v-avatar v-bind="attrs" v-on="on" size="36" class="drawer-avatar drawer-avatar--mini">
              <span class="avatar-initials avatar-initials--mini">{{ userInitials }}</span>
            </v-avatar>
          </template>
          <span>{{ loggedInUser?.data?.username }}</span>
        </v-tooltip>
      </div>
    </div>

    <!-- Navigation Section -->
    <div class="nav-scroll-area">
      <v-list class="nav-list" dense>
        <template v-for="(item, i) in items">
          <!-- Section Label -->
          <div v-if="item.section && hasVisibleItems(item.section)" :key="'section-' + i" class="nav-section">
            <div v-if="drawer" class="nav-section-label" @click="toggleSection(item.section)">
              <div class="section-icon-wrapper">
                <v-icon size="14">{{ item.icon }}</v-icon>
              </div>
              <span class="section-text">{{ item.section }}</span>
              <v-icon size="16" class="section-arrow" :class="{ 'section-arrow--collapsed': isSectionCollapsed(item.section) }">
                mdi-chevron-up
              </v-icon>
            </div>
            <div v-else class="nav-section-mini">
              <v-tooltip right :open-delay="200">
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on" class="section-icon-mini" @click="toggleSection(item.section)">
                    <v-icon size="14">{{ item.icon }}</v-icon>
                  </div>
                </template>
                <span>{{ item.section }}</span>
              </v-tooltip>
            </div>
          </div>

          <!-- Menu Link Item -->
          <div v-else-if="(item.to || item.children) && isItemVisible(i)" :key="i" class="nav-item-wrapper">
            <div v-if="isAllowed(item)">
              <template v-if="item.children && drawer">
                <button
                    type="button"
                    :class="['nav-link', 'nav-link--group', { 'nav-link--active': isParentRouteActive(item) }]"
                    @click="handleMenuItemClick(item)"
                >
                  <div class="nav-icon-wrapper">
                    <v-icon size="18">{{ item.icon }}</v-icon>
                  </div>
                  <div class="nav-link-group-content">
                    <span class="nav-link-title">{{ item.title }}</span>
                    <span class="nav-link-subtitle">
                      {{ getVisibleChildren(item).length }} options
                    </span>
                  </div>
                  <v-icon
                      size="16"
                      class="submenu-arrow"
                      :class="{ 'submenu-arrow--collapsed': isMenuGroupCollapsed(item.title) }"
                  >
                    mdi-chevron-up
                  </v-icon>
                </button>
                <div v-show="!isMenuGroupCollapsed(item.title)" class="nav-submenu">
                  <button
                      v-for="child in getVisibleChildren(item)"
                      :key="`${item.title}-${child.title}`"
                      type="button"
                      :class="['nav-submenu-link', { 'nav-submenu-link--active': isRouteActive(child.to || item.to, child.query) }]"
                      @click="navigateToSubmenu(item, child)"
                  >
                    <span class="nav-submenu-bullet"></span>
                    <span class="nav-submenu-title">{{ child.title }}</span>
                  </button>
                </div>
              </template>
              <v-tooltip v-else-if="!drawer" right :open-delay="200">
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item
                      v-bind="attrs"
                      v-on="on"
                      :class="['nav-link', { 'nav-link--active': isRouteActive(item.to), 'nav-link--mini': !drawer }]"
                      @click="handleMenuItemClick(item)"
                      :ripple="{ class: 'nav-ripple' }"
                  >
                    <div class="nav-icon-wrapper">
                      <v-icon size="18">{{ item.icon }}</v-icon>
                    </div>
                  </v-list-item>
                </template>
                <span class="tooltip-text">{{ item.title }}</span>
              </v-tooltip>
              <v-list-item
                  v-else
                  :class="['nav-link', { 'nav-link--active': isRouteActive(item.to) }]"
                  @click="handleMenuItemClick(item)"
                  :ripple="{ class: 'nav-ripple' }"
              >
                <div class="nav-icon-wrapper">
                  <v-icon size="18">{{ item.icon }}</v-icon>
                </div>
                <v-list-item-content>
                  <v-list-item-title class="nav-link-title">{{ item.title }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </div>
          </div>
        </template>
      </v-list>
    </div>

    <!-- Bottom Actions -->
    <template v-slot:append>
      <div class="bottom-section">
        <div class="bottom-divider"></div>
        <div class="bottom-actions" :class="{ 'bottom-actions--mini': !drawer }">
          <v-tooltip v-if="!drawer" right :open-delay="200">
            <template v-slot:activator="{ on, attrs }">
              <button v-bind="attrs" v-on="on" class="bottom-action-btn bottom-action-btn--mini" @click="openChangePass()">
                <span class="bottom-action-icon">
                  <v-icon size="18">mdi-lock-reset</v-icon>
                </span>
              </button>
            </template>
            <span>Change Password</span>
          </v-tooltip>
          <button v-else class="bottom-action-btn" @click="openChangePass()">
            <span class="bottom-action-icon">
              <v-icon size="18">mdi-lock-reset</v-icon>
            </span>
            <span class="bottom-action-text">Change Password</span>
            <v-icon size="14" class="bottom-action-arrow">mdi-chevron-right</v-icon>
          </button>

          <v-tooltip v-if="!drawer" right :open-delay="200">
            <template v-slot:activator="{ on, attrs }">
              <button v-bind="attrs" v-on="on" class="bottom-action-btn bottom-action-btn--mini bottom-action-btn--logout" @click="logout()">
                <span class="bottom-action-icon">
                  <v-icon size="18">mdi-logout</v-icon>
                </span>
              </button>
            </template>
            <span>Logout</span>
          </v-tooltip>
          <button v-else class="bottom-action-btn bottom-action-btn--logout" @click="logout()">
            <span class="bottom-action-icon bottom-action-icon--logout">
              <v-icon size="18">mdi-logout</v-icon>
            </span>
            <span class="bottom-action-text">Logout</span>
            <v-icon size="14" class="bottom-action-arrow">mdi-chevron-right</v-icon>
          </button>
        </div>

        <!-- Version Info -->
        <div v-if="drawer" class="version-info">
          <span>v2.0.0</span>
        </div>
      </div>
    </template>

    <!-- Change Password Dialog -->
    <template>
      <v-dialog v-model="changePasswordDialog" persistent width="460">
        <v-card class="dialog-card">
          <v-card-title class="dialog-header" primary-title>
            <v-icon size="22" class="mr-3">mdi-lock-reset</v-icon>
            <span>Change Password</span>
            <v-spacer></v-spacer>
            <v-btn color="white" icon text @click="closeDialog()" class="dialog-close-btn">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <ChangePassword
              v-if="changePasswordDialog"
              @close="changePasswordDialog = false"
          ></ChangePassword>
        </v-card>
      </v-dialog>
    </template>
    </v-navigation-drawer>
  </div>
</template>

<script>
import {accessPolicy} from "@/utils/managedAccess";
import {canAccessMenuItem, getUser} from "@/utils/helper";
import ChangePassword from "../../components/Login/ChangePassword.vue";
import {LOGOUT} from "@/store/actions.type";

export default {
  name: "DashboardCoreDrawer",

  props: {
    expandOnHover: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ChangePassword,
  },

  data: () => ({
    loggedInUser: "",
    drawerColor: "transparent",
    barColor: "hsla(0,0%,100%,.45)",
    drawer: true,
    drawerVisible: true,
    items: [
      { section: "Overview", icon: "mdi-view-dashboard-outline" },
      { icon: "mdi-monitor-dashboard", title: "Dashboard", to: "/dashboard" },
      { icon: "mdi-school-outline", title: "E-learning", to: "/e-learning" },
      { icon: "mdi-connection", title: "E-learning integrations", to: "/e-learning/integrations" },
      { icon: "mdi-school-outline", title: "PreService", to: "/preservice" },
      { icon: "mdi-map-marker-outline", title: "Map", to: "/map" },
      { icon: "mdi-access-point", title: "Online Status", to: "/onlineStatus" },

      { section: "Management", icon: "mdi-clipboard-text-outline" },
      {
        icon: "mdi-school-outline",
        title: "Pre-service",
        to: "/pre-service",
        accessAliases: ["Pre-service", "/pre-service"],
        children: [
          { title: "TVET Program", to: "/pre-service/tvet-program", accessAliases: ["Pre-service", "/pre-service"] },
          { title: "Generic Entry", to: "/pre-service/generic-entry", accessAliases: ["Pre-service", "/pre-service"] },
          { title: "Post Basic", to: "/pre-service/post-basic", accessAliases: ["Pre-service", "/pre-service"] },
          { title: "Post Graduate", to: "/pre-service/post-graduate", accessAliases: ["Pre-service", "/pre-service"] },
          { title: "Graduate Entry", to: "/pre-service/graduate-entry", accessAliases: ["Pre-service", "/pre-service"] },
        ],
      },
      { icon: "mdi-shield-account-outline", title: "CPD Accreditor", to: "/accreditor" },
      { icon: "mdi-domain", title: "CPD Provider", to: "/cpdProvider" },
      { icon: "mdi-book-open-outline", title: "Course", to: "/course" },
      { icon: "mdi-clipboard-check-outline", title: "CPD Activity", to: "/cpdActivity" },
      { icon: "mdi-account-tie-outline", title: "Trainer", to: "/trainer" },
      { icon: "mdi-account-school-outline", title: "Trainee", to: "/traineeRegistration" },
      { icon: "mdi-calendar-check-outline", title: "Program Event", to: "/programEvent" },

      { section: "Administration", icon: "mdi-shield-lock-outline" },
      { icon: "mdi-account-key-outline", title: "Roles", to: "/role", accessAliases: ["User Groups", "Permissions"] },
      { icon: "mdi-account-multiple-outline", title: "Users", to: "/user" },
      { icon: "mdi-menu", title: "Menu Items", to: "/menuItem" },

      { section: "Reports", icon: "mdi-file-document-outline" },
      { icon: "mdi-calendar-text-outline", title: "Training Event Report", to: "/trainingEventReport" },
      { icon: "mdi-file-chart-outline", title: "CPD Provider Report", to: "/cpdReport" },
      { icon: "mdi-file-certificate-outline", title: "CPD Accreditor Report", to: "/accreditorReport" },
      { icon: "mdi-certificate-outline", title: "Accredited Course Report", to: "/accreditedCourseReport" },
      { icon: "mdi-chart-box-outline", title: "Accreditation Summary Report", to: "/accreditationSummaryReport" },
      { icon: "mdi-text-box-outline", title: "Training Detail Report", to: "/trainingDetailReport" },
      { icon: "mdi-school-outline", title: "Preservice Report", to: "/preserviceReport" },
      { icon: "mdi-autorenew", title: "Renewal Status Report", to: "/renewalStatusReport" },
      { icon: "mdi-account-school-outline", title: "Trainee Report", to: "/traineeReport" },
      { icon: "mdi-account-tie-outline", title: "Trainer Report", to: "/trainerReport" },
      { icon: "mdi-book-outline", title: "Course Report", to: "/courseReport" },
      { icon: "mdi-chart-timeline-variant", title: "Monitoring", to: "/graduateTracking" },

      { section: "Lookups", icon: "mdi-database-outline" },
      { icon: "mdi-medical-bag", title: "Health Professions", to: "/healthProfessions" },
      { icon: "mdi-hospital-building", title: "Mfr", to: "/mfr" },
      { icon: "mdi-town-hall", title: "Universities", to: "/universities" },
      { icon: "mdi-certificate-outline", title: "Certificate Reasons", to: "/certificateReasons" },
      { icon: "mdi-book-education-outline", title: "Program Education", to: "/educationalPrograms" },
      { icon: "mdi-shape-outline", title: "Disciplines", to: "/disciplines" },
      { icon: "mdi-tag-multiple-outline", title: "CPD Activity Category", to: "/cpdActivityCategory" },
      { icon: "mdi-clipboard-list-outline", title: "Cat II Requirement", to: "/catIIRequirement" },
      { icon: "mdi-stairs-up", title: "Level of Studies", to: "/levelOfStudies" },
      { icon: "mdi-account-group-outline", title: "Ethnicity", to: "/ethnicity" },
      { icon: "mdi-domain", title: "Organization", to: "/organizations" },
      { icon: "mdi-clock-outline", title: "Config", to: "/generalTimes" },
      { icon: "mdi-office-building-outline", title: "Facilities", to: "/Facilities" },
      { icon: "mdi-checkbox-marked-circle-outline", title: "Trainer Selection Criteria", to: "/trainerSelectionCriteria" },
      { icon: "mdi-home-city-outline", title: "Trainee Facilities", to: "/traineeFacilities" },
    ],
    changePasswordDialog: false,
    collapsedSections: {},
    collapsedMenuGroups: {},
  }),

  computed: {
    userInitials() {
      const username = this.loggedInUser?.data?.username || '';
      return username.substring(0, 2).toUpperCase();
    },
  },

  async created() {
    this.loggedInUser = getUser();
    // Collapse all sections by default on load
    this.collapseAllSections();
    this.initializeMenuGroups();
  },

  watch: {
    "$route.fullPath"() {
      this.syncMenuGroupsWithRoute();
    },
  },

  methods: {
    collapseAllSections() {
      const allSections = this.items
        .filter(item => item.section)
        .map(item => item.section);
      allSections.forEach(section => {
        this.$set(this.collapsedSections, section, true);
      });
    },
    isRouteActive(target, query = {}) {
      if (!this.$route || this.$route.path !== target) {
        return false;
      }

      return Object.keys(query).every((key) => {
        return `${this.$route.query?.[key] || ""}` === `${query[key]}`;
      });
    },
    initializeMenuGroups() {
      this.items
        .filter((item) => item.children && item.children.length)
        .forEach((item) => {
          this.$set(this.collapsedMenuGroups, item.title, !this.isParentRouteActive(item));
        });
    },
    syncMenuGroupsWithRoute() {
      this.items
        .filter((item) => item.children && item.children.length)
        .forEach((item) => {
          if (this.isParentRouteActive(item)) {
            this.$set(this.collapsedMenuGroups, item.title, false);
          }
        });
    },
    isMenuGroupCollapsed(groupTitle) {
      return this.collapsedMenuGroups[groupTitle] !== false;
    },
    toggleMenuGroup(groupTitle) {
      this.$set(this.collapsedMenuGroups, groupTitle, !this.isMenuGroupCollapsed(groupTitle));
    },
    isParentRouteActive(item) {
      if (!item.children || !item.children.length) {
        return this.isRouteActive(item.to);
      }

      return this.getVisibleChildren(item).some((child) =>
        this.isRouteActive(child.to || item.to, child.query)
      )
        || this.isRouteActive(item.to);
    },
    getActiveChild(item) {
      if (!item.children || !item.children.length) {
        return null;
      }

      return this.getVisibleChildren(item).find((child) =>
        this.isRouteActive(child.to || item.to, child.query)
      ) || null;
    },
    getVisibleChildren(item) {
      if (!item.children || !item.children.length) {
        return [];
      }

      return item.children.filter((child) => this.isAllowed(child));
    },
    navigateToRoute(target, query = undefined) {
      if (!target || this.isRouteActive(target, query || {})) {
        return;
      }

      this.$router.push({path: target, query}).catch((error) => {
        if (error?.name !== "NavigationDuplicated") {
          throw error;
        }
      });
    },
    navigateToSubmenu(parent, child) {
      this.$set(this.collapsedMenuGroups, parent.title, false);
      this.navigateToRoute(child.to || parent.to, child.query);
    },
    handleMenuItemClick(item) {
      if (item.children && item.children.length) {
        if (!this.drawer) {
          const targetChild = this.getActiveChild(item) || this.getVisibleChildren(item)[0];
          if (targetChild) {
            this.navigateToSubmenu(item, targetChild);
          }
          return;
        }

        this.toggleMenuGroup(item.title);
        return;
      }

      this.navigateToRoute(item.to);
    },
    isAllowed(item) {
      const user = getUser();
      const roleName = user?.data?.userRoles?.roleName;

      if (item.title === "Config" && !accessPolicy(user)) {
        return roleName === "System admins";
      }

      return canAccessMenuItem(user, item);
    },
    hasVisibleItems(sectionName) {
      const sectionIndex = this.items.findIndex((item) => item.section === sectionName);
      if (sectionIndex === -1) {
        return false;
      }

      for (let index = sectionIndex + 1; index < this.items.length; index += 1) {
        const item = this.items[index];
        if (item.section) {
          break;
        }
        if (item.to && this.isAllowed(item)) {
          return true;
        }
      }

      return false;
    },
    toggleSection(sectionName) {
      const isCurrentlyCollapsed = this.collapsedSections[sectionName];

      // Get all section names
      const allSections = this.items
        .filter(item => item.section)
        .map(item => item.section);

      // Collapse all sections first
      allSections.forEach(section => {
        this.$set(this.collapsedSections, section, true);
      });

      // If the clicked section was collapsed, expand it (others stay collapsed)
      // If it was expanded, it's now collapsed along with others
      if (isCurrentlyCollapsed) {
        this.$set(this.collapsedSections, sectionName, false);
      }
    },
    isSectionCollapsed(sectionName) {
      return this.collapsedSections[sectionName] === true;
    },
    getCurrentSection(index) {
      for (let i = index - 1; i >= 0; i--) {
        if (this.items[i].section) {
          return this.items[i].section;
        }
      }
      return null;
    },
    isItemVisible(index) {
      const section = this.getCurrentSection(index);
      return section ? !this.isSectionCollapsed(section) : true;
    },
    closeDialog() {
      this.changePasswordDialog = false;
    },
    openChangePass() {
      this.changePasswordDialog = true;
    },
    logout() {
      this.$store.dispatch(LOGOUT).then(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        this.$router.push("/login");
      });
    },
    toggleDrawer() {
      this.drawer = !this.drawer;
      this.drawerVisible = true;
    },
  },
};
</script>

<style scoped>
/* ===== Base Drawer Styles ===== */
.app-drawer {
  --drawer-text-primary: #ffffff;
  --drawer-text-secondary: #e2e8f0;
  --drawer-text-muted: #cbd5e1;
  --drawer-text-subtle: #bfd0e3;
  --drawer-icon-default: #dbeafe;
  background: linear-gradient(180deg, #3a4f63 0%, #2d3f4f 100%) !important;
  color: var(--drawer-text-secondary);
  border-right: none !important;
  box-shadow: 4px 0 32px rgba(0, 0, 0, 0.15);
}

.app-drawer::before {
  content: "";
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  opacity: 0.02;
  pointer-events: none;
}

.app-drawer :deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

/* Custom Scrollbar */
.app-drawer :deep(.v-navigation-drawer__content::-webkit-scrollbar),
.nav-scroll-area::-webkit-scrollbar {
  width: 4px;
}

.app-drawer :deep(.v-navigation-drawer__content::-webkit-scrollbar-track),
.nav-scroll-area::-webkit-scrollbar-track {
  background: transparent;
}

.app-drawer :deep(.v-navigation-drawer__content::-webkit-scrollbar-thumb),
.nav-scroll-area::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 4px;
}

.app-drawer :deep(.v-navigation-drawer__content::-webkit-scrollbar-thumb:hover),
.nav-scroll-area::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* ===== Brand Section ===== */
.brand-section {
  padding: 16px 14px;
  flex-shrink: 0;
}

.brand-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.brand-logo {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.brand-text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.brand-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--drawer-text-primary);
  letter-spacing: 0.5px;
}

.brand-tagline {
  font-size: 0.7rem;
  color: var(--drawer-text-muted);
  letter-spacing: 0.3px;
  margin-top: 1px;
}

.collapse-btn {
  background: rgba(255, 255, 255, 0.06) !important;
  color: var(--drawer-text-muted) !important;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.collapse-btn:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  color: #ffffff !important;
}

.brand-mini {
  display: flex;
  justify-content: center;
  padding: 4px 0;
}

.expand-btn {
  background: rgba(255, 255, 255, 0.08) !important;
  color: var(--drawer-text-secondary) !important;
  border-radius: 10px;
  transition: all 0.2s ease;
}

.expand-btn:hover {
  background: rgba(255, 255, 255, 0.15) !important;
  transform: scale(1.05);
}

/* ===== User Section ===== */
.user-section {
  padding: 0 14px 12px;
  flex-shrink: 0;
}

.user-section--mini {
  padding: 8px 14px 12px;
  display: flex;
  justify-content: center;
}

.user-profile-card {
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(10px);
  border-radius: 14px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.user-profile-card:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  transform: translateY(-1px);
}

.user-profile-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.drawer-avatar {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%) !important;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.35);
  font-weight: 600;
}

.drawer-avatar--mini {
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  cursor: pointer;
  transition: all 0.2s ease;
}

.drawer-avatar--mini:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.avatar-initials {
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.avatar-initials--mini {
  font-size: 0.75rem;
}

.status-indicator {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  background: linear-gradient(135deg, #34d399, #10b981);
  border: 2px solid #3a4f63;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.4);
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.user-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.drawer-user-label {
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--drawer-text-subtle);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.drawer-username {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--drawer-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

/* ===== Navigation Scroll Area ===== */
.nav-scroll-area {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* ===== Navigation List ===== */
.nav-list {
  padding: 4px 10px 16px;
  background: transparent !important;
}

.nav-item-wrapper {
  margin: 2px 0;
  animation: fadeSlideIn 0.2s ease;
}

@keyframes fadeSlideIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== Section Labels ===== */
.nav-section {
  margin-top: 16px;
  margin-bottom: 8px;
}

.nav-section:first-child {
  margin-top: 4px;
}

.nav-section-label {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  margin: 0 4px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s ease;
}

.nav-section-label:hover {
  background: rgba(255, 255, 255, 0.04);
}

.section-icon-wrapper {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: rgba(96, 165, 250, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
}

.section-icon-wrapper :deep(.v-icon) {
  color: #60a5fa;
}

.section-text {
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--drawer-text-muted);
  text-transform: uppercase;
  letter-spacing: 1px;
  flex: 1;
}

.section-arrow {
  color: var(--drawer-text-subtle);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  margin-left: auto;
}

.section-arrow--collapsed {
  transform: rotate(180deg);
}

.nav-section-label:hover .section-arrow {
  color: #94a3b8;
}

.nav-section-mini {
  display: flex;
  justify-content: center;
  padding: 8px 0;
  margin: 0 4px;
}

.section-icon-mini {
  width: 32px;
  height: 24px;
  border-radius: 6px;
  background: rgba(96, 165, 250, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}

.section-icon-mini :deep(.v-icon) {
  color: #60a5fa;
}

/* ===== Navigation Links ===== */
.nav-link {
  color: var(--drawer-text-secondary) !important;
  border-radius: 10px;
  margin: 2px 4px;
  padding: 10px 12px;
  min-height: 44px;
  height: auto !important;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: visible;
}

.nav-link--group {
  width: calc(100% - 8px);
  border: none;
  text-align: left;
  cursor: pointer;
}

.nav-link :deep(.v-list-item__content) {
  flex: 1;
  min-width: 0;
  overflow: visible;
  align-self: center;
}

.nav-link :deep(.v-list-item__icon) {
  align-self: flex-start;
  margin-top: 2px;
}

.nav-link::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%) scaleY(0);
  width: 3px;
  height: 24px;
  border-radius: 0 4px 4px 0;
  background: linear-gradient(180deg, #60a5fa, #3b82f6);
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 0 8px rgba(59, 130, 246, 0.5);
}

.nav-icon-wrapper {
  margin-right: 12px !important;
  min-width: 32px !important;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.2s ease;
}

.nav-icon-wrapper :deep(.v-icon) {
  color: var(--drawer-icon-default);
  transition: all 0.2s ease;
}

.nav-link--mini {
  padding: 12px;
  justify-content: center;
  margin: 4px auto;
  width: 44px;
}

.nav-link--mini .nav-icon-wrapper {
  margin-right: 0 !important;
}

.nav-link:hover {
  background: rgba(255, 255, 255, 0.06);
  color: var(--drawer-text-primary) !important;
}

.nav-link:hover .nav-icon-wrapper :deep(.v-icon) {
  color: var(--drawer-text-primary);
  transform: scale(1.08);
}

.nav-link:hover .nav-icon-wrapper {
  background: rgba(255, 255, 255, 0.1);
}

.nav-link--active {
  background: rgba(96, 165, 250, 0.15) !important;
  color: var(--drawer-text-primary) !important;
}

.nav-link--active::before {
  transform: translateY(-50%) scaleY(1);
}

.nav-link--active .nav-icon-wrapper :deep(.v-icon) {
  color: #60a5fa !important;
}

.nav-link--active .nav-icon-wrapper {
  background: rgba(255, 255, 255, 0.14);
}

.nav-link-title {
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.2px;
  transition: all 0.2s ease;
  color: inherit;
  white-space: normal;
  word-wrap: break-word;
  line-height: 1.4;
}

.nav-link-group-content {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
}

.nav-link-subtitle {
  font-size: 0.68rem;
  color: var(--drawer-text-muted);
  letter-spacing: 0.4px;
  margin-top: 2px;
}

.nav-link--active .nav-link-title {
  font-weight: 600;
  color: var(--drawer-text-primary);
}

.nav-link--active .nav-link-subtitle {
  color: #bfdbfe;
}

.submenu-arrow {
  color: var(--drawer-text-muted);
  transition: transform 0.2s ease, color 0.2s ease;
}

.submenu-arrow--collapsed {
  transform: rotate(180deg);
}

.nav-link:hover .submenu-arrow,
.nav-link--active .submenu-arrow {
  color: #cbd5e1;
}

.nav-submenu {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 4px 4px 8px 50px;
  padding-left: 14px;
  border-left: 1px solid rgba(148, 163, 184, 0.18);
}

.nav-submenu-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: var(--drawer-text-secondary);
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
}

.nav-submenu-link:hover {
  background: rgba(255, 255, 255, 0.05);
  color: var(--drawer-text-primary);
}

.nav-submenu-link--active {
  background: rgba(96, 165, 250, 0.12);
  color: var(--drawer-text-primary);
}

.nav-submenu-bullet {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: currentColor;
  opacity: 0.5;
  flex-shrink: 0;
}

.nav-submenu-link--active .nav-submenu-bullet {
  opacity: 1;
}

.nav-submenu-title {
  font-size: 0.8rem;
  line-height: 1.3;
}

/* ===== Bottom Section ===== */
.bottom-section {
  flex-shrink: 0;
}

.bottom-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent);
  margin: 0 16px;
}

.bottom-actions {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.bottom-actions--mini {
  align-items: center;
}

.bottom-action-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  color: var(--drawer-text-secondary);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: left;
  position: relative;
  overflow: hidden;
}

.bottom-action-btn--mini {
  width: 44px;
  height: 44px;
  padding: 0;
  justify-content: center;
}

.bottom-action-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--drawer-text-primary);
}

.bottom-action-btn:hover .bottom-action-arrow {
  opacity: 1;
  transform: translateX(0);
}

.bottom-action-btn--logout:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #fca5a5;
}

.bottom-action-btn--logout:hover .bottom-action-icon--logout :deep(.v-icon) {
  color: #fca5a5;
}

.bottom-action-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.bottom-action-icon :deep(.v-icon) {
  color: var(--drawer-icon-default);
  transition: color 0.2s ease;
}

.bottom-action-btn:hover .bottom-action-icon {
  background: rgba(255, 255, 255, 0.1);
}

.bottom-action-btn:hover .bottom-action-icon :deep(.v-icon) {
  color: var(--drawer-text-primary);
}

.bottom-action-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bottom-action-arrow {
  color: var(--drawer-text-muted);
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.2s ease;
}

/* Version Info */
.version-info {
  padding: 8px 16px 12px;
  text-align: center;
  font-size: 0.65rem;
  color: var(--drawer-text-subtle);
  letter-spacing: 0.5px;
}

/* ===== Dialog Styles ===== */
.dialog-header {
  background: linear-gradient(135deg, #3a4f63, #2d3f4f);
  color: #e2e8f0;
  padding: 16px 20px;
}

.dialog-close-btn {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.dialog-close-btn:hover {
  opacity: 1;
}

.dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.25);
}

/* ===== Tooltip Styles ===== */
.tooltip-text {
  font-size: 0.8rem;
  font-weight: 500;
}

/* ===== Ripple Effect ===== */
:deep(.nav-ripple) {
  background: rgba(255, 255, 255, 0.1) !important;
}

/* ===== Animations ===== */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
    box-shadow: 0 2px 6px rgba(16, 185, 129, 0.4);
  }
  50% {
    opacity: 0.7;
    box-shadow: 0 2px 10px rgba(16, 185, 129, 0.6);
  }
}

/* ===== Transition Classes ===== */
.nav-link,
.nav-sublink,
.bottom-action-btn {
  will-change: transform, background-color;
}
</style>
